import { z } from 'zod';
import { IsoDateTime, Uuid, patchOf } from './common';
import { CreateCustomerBody } from './customers';
import { StoredFile } from './files';
import { PricingCalculateRequest } from './pricing';
import { JobStatus } from './queue';
import { QuoteDeposit } from './quotes';

const Centavos = z.number().int().min(0).max(100_000_000);

export const OrderStatus = z.enum([
  'new',
  'in_production',
  'to_pack',
  'shipped',
  'closed',
  'cancelled',
  'returned',
]);
export type OrderStatus = z.infer<typeof OrderStatus>;
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  new: 'Novo',
  in_production: 'Em produção',
  to_pack: 'Para embalar',
  shipped: 'Enviado',
  closed: 'Fechado',
  cancelled: 'Cancelado',
  returned: 'Devolvido',
};
/** The main flow (cancelled and returned branch off it). */
export const ORDER_FLOW: OrderStatus[] = ['new', 'in_production', 'to_pack', 'shipped', 'closed'];

export const ShippingMethod = z.enum(['mercado_envios', 'correios', 'pickup', 'hand']);
export type ShippingMethod = z.infer<typeof ShippingMethod>;
export const SHIPPING_METHOD_LABELS: Record<ShippingMethod, string> = {
  mercado_envios: 'Mercado Envios',
  correios: 'Correios',
  pickup: 'Retirada',
  hand: 'Entrega em mãos',
};
/** Methods with a label and tracking code. */
export const POSTED_METHODS: ShippingMethod[] = ['mercado_envios', 'correios'];

export const NfStatus = z.enum(['not_needed', 'pending', 'issued']);
export type NfStatus = z.infer<typeof NfStatus>;
export const NF_STATUS_LABELS: Record<NfStatus, string> = {
  not_needed: 'Não precisa',
  pending: 'Pendente',
  issued: 'Emitida',
};

export const ChecklistKey = z.enum(['packed', 'nf', 'label', 'delivered']);
export type ChecklistKey = z.infer<typeof ChecklistKey>;

export const CancelReason = z.enum(['gave_up', 'no_capacity', 'listing_error', 'other']);
export const CANCEL_REASON_LABELS: Record<z.infer<typeof CancelReason>, string> = {
  gave_up: 'Cliente desistiu',
  no_capacity: 'Sem material ou máquina',
  listing_error: 'Erro no anúncio',
  other: 'Outro',
};
export const ReturnReason = z.enum(['defect', 'broke_in_transit', 'regret', 'other']);
export const RETURN_REASON_LABELS: Record<z.infer<typeof ReturnReason>, string> = {
  defect: 'Chegou com defeito',
  broke_in_transit: 'Quebrou no transporte',
  regret: 'Arrependimento (7 dias)',
  other: 'Outro',
};

/** The 44-digit NF-e access key ("chave de acesso"), digits only. */
export const NfKey = z
  .string()
  .transform((s) => s.replace(/\D/g, ''))
  .superRefine((s, ctx) => {
    if (s.length !== 44) {
      ctx.addIssue({
        code: 'custom',
        message: `A chave tem 44 dígitos. Você digitou ${s.length}.`,
      });
    }
  });

// ---- Bodies --------------------------------------------------------------------------------

export const OrderItemInput = z
  .object({
    variantId: Uuid.optional(),
    quoteItemId: Uuid.optional().describe(
      'An item of an accepted quote: its frozen calculation is used.',
    ),
    request: PricingCalculateRequest.optional().describe(
      'A one-off calculation (Quick Quote "Converter em pedido"); needs `description`.',
    ),
    quantity: z
      .number()
      .int()
      .min(1)
      .max(100_000)
      .optional()
      .describe('Defaults to the quote item quantity, the request quantity, else 1.'),
    unitPriceCentavos: Centavos.nullable()
      .optional()
      .describe(
        'Defaults to the listed price on the channel, else the suggestion (variant) or the quote price.',
      ),
    description: z.string().trim().min(1).max(200).optional(),
    slicedFileId: Uuid.nullable().optional(),
    thumbnailFileId: Uuid.nullable().optional(),
  })
  .refine(
    (i) => [i.variantId, i.quoteItemId, i.request].filter((x) => x !== undefined).length === 1,
    { message: 'Send one of variantId, quoteItemId or request' },
  )
  .refine((i) => i.request === undefined || i.description !== undefined, {
    message: 'Describe the item',
    path: ['description'],
  });
export type OrderItemInput = z.input<typeof OrderItemInput>;

const RealCharges = z.object({
  commissionCentavos: Centavos.optional(),
  fixedFeeCentavos: Centavos.optional(),
  shippingCentavos: Centavos.optional().describe('Shipping paid by the shop.'),
  taxCentavos: Centavos.optional(),
  discountsCentavos: Centavos.optional().describe('Coupons and discounts the shop paid.'),
});

const OrderFields = z.object({
  channelId: Uuid,
  paymentMethod: z
    .string()
    .trim()
    .max(60)
    .nullable()
    .default(null)
    .describe('E.g. "Pix" on a direct sale.'),
  marketplaceOrderNo: z.string().trim().max(80).nullable().default(null),
  soldOn: z.iso.date().optional().describe('Defaults to today (São Paulo).'),
  customerId: Uuid.nullable().default(null),
  newCustomer: CreateCustomerBody.optional(),
  shipBy: z.iso.date().nullable().default(null).describe('"Prazo de envio".'),
  shippingMethod: ShippingMethod.nullable().default(null),
  trackingCode: z.string().trim().max(80).nullable().default(null),
  nfStatus: NfStatus.default('not_needed'),
  nfNumber: z.string().trim().max(20).nullable().default(null),
  packagingKitId: Uuid.nullable().optional().describe('Defaults to the channel kit.'),
  kitQuantity: z.number().int().min(0).max(1000).default(1),
  notes: z.string().trim().max(2000).nullable().default(null),
  externalRef: z.string().trim().max(200).nullable().default(null),
  charges: RealCharges.default({}).describe(
    'Real charges from the sale; missing ones use the prediction.',
  ),
});

export const CreateOrderBody = OrderFields.extend({
  quoteId: Uuid.nullable()
    .default(null)
    .describe('The accepted quote this order comes from (it becomes Converted).'),
  items: z.array(OrderItemInput).min(1).max(50),
  confirm: z
    .boolean()
    .default(true)
    .describe('Reserve Pronta entrega and queue the missing units now (false = stays Novo).'),
}).meta({ id: 'CreateOrderBody' });
export type CreateOrderBody = z.input<typeof CreateOrderBody>;

export const UpdateOrderBody = patchOf(
  OrderFields.omit({ channelId: true, newCustomer: true, charges: true }).extend({
    nfKey: NfKey.nullable(),
    nfIssuedOn: z.iso.date().nullable(),
    labelFileId: Uuid.nullable(),
    nfXmlFileId: Uuid.nullable(),
    nfPdfFileId: Uuid.nullable(),
  }),
).extend({ charges: RealCharges.optional() });
export type UpdateOrderBody = z.input<typeof UpdateOrderBody>;

export const ShipOrderBody = z.object({
  trackingCode: z.string().trim().max(80).nullable().default(null),
});
export const ChecklistBody = z.object({ done: z.boolean() });
export const EndOrderBody = z.object({
  reason: z.string().trim().min(1).max(40),
  note: z.string().trim().max(500).nullable().default(null),
  refundCentavos: Centavos,
  backToStock: z.boolean(),
});
export const EndPreview = z.object({
  spentCentavos: z.number().int().describe('Cost already spent on the order.'),
  recoveredCentavos: z.number().int().describe('Cost that comes back as Pronta entrega.'),
  keptCentavos: z.number().int().describe('Revenue kept (total − refund).'),
  lossCentavos: z.number().int().describe('max(0, spent − recovered − kept).'),
});
export const ConvertQuoteBody = OrderFields.omit({
  channelId: true,
  customerId: true,
  newCustomer: true,
})
  .partial()
  .extend({ confirm: z.boolean().default(true) });

// ---- DTOs ----------------------------------------------------------------------------------

export const OrderItem = z.object({
  id: Uuid,
  description: z.string(),
  variant: z
    .object({
      id: Uuid,
      name: z.string(),
      productId: Uuid,
      productName: z.string(),
      sku: z.string(),
    })
    .nullable(),
  quoteItemId: Uuid.nullable(),
  quantity: z.number().int(),
  unitPriceCentavos: z.number().int(),
  priceSource: z.enum(['suggested', 'override']),
  unitCostCentavos: z.number().int(),
  netProfitCentavos: z.number().int().describe('Per unit, estimated.'),
  fromStockUnits: z.number().int().describe('Taken from Pronta entrega.'),
  producedUnits: z.number().int().describe('Printed for this order so far.'),
  queuedUnits: z.number().int().describe('In jobs queued or printing.'),
  missingUnits: z
    .number()
    .int()
    .describe('Neither in stock, produced nor queued (e.g. after defects).'),
  grams: z.number().describe('Per unit, estimated.'),
  printSeconds: z.number().int().describe('Per unit, estimated.'),
  thumbnailUrl: z.string().nullable(),
});
export type OrderItem = z.infer<typeof OrderItem>;

export const OrderJob = z.object({
  id: Uuid,
  number: z.string(),
  status: JobStatus,
  units: z.number().int().describe('Units of this order in the job.'),
  plateIndex: z.number().int(),
  estSeconds: z.number().int(),
  actualSeconds: z.number().int().nullable(),
  estGrams: z.number(),
  actualGrams: z.number().nullable(),
  failPct: z.number().int().nullable(),
  failReason: z.string().nullable(),
  redoOf: z.string().nullable(),
  material: z.string().nullable(),
});

export const ProfitRow = z.object({
  key: z.enum(['grams', 'hours', 'failures', 'fees', 'cost']),
  label: z.string(),
  estimated: z.number(),
  real: z.number(),
  unit: z.enum(['g', 'h', 'centavos']),
});
export const OrderProfit = z.object({
  totalCentavos: z.number().int(),
  estimatedCostCentavos: z.number().int(),
  estimatedFeesCentavos: z.number().int(),
  estimatedProfitCentavos: z.number().int(),
  estimatedMarginBps: z.number().int(),
  realCostCentavos: z.number().int(),
  realFeesCentavos: z.number().int(),
  realProfitCentavos: z.number().int(),
  realMarginBps: z.number().int(),
  final: z.boolean().describe('Frozen when the order closed (or ended).'),
  rows: z.array(ProfitRow),
});
export type OrderProfit = z.infer<typeof OrderProfit>;

export const OrderCharges = z.object({
  commissionCentavos: z.number().int(),
  fixedFeeCentavos: z.number().int(),
  shippingCentavos: z.number().int(),
  taxCentavos: z.number().int(),
  discountsCentavos: z.number().int(),
});

export const Order = z
  .object({
    id: Uuid,
    number: z.string().describe('"P-2026-0001".'),
    status: OrderStatus,
    late: z.boolean().describe('Not shipped and past the ship-by date.'),
    channel: z.object({ id: Uuid, name: z.string(), color: z.string().nullable() }),
    paymentMethod: z.string().nullable(),
    marketplaceOrderNo: z.string().nullable(),
    soldOn: z.iso.date(),
    customer: z.object({ id: Uuid, name: z.string(), whatsapp: z.string().nullable() }).nullable(),
    quote: z
      .object({
        id: Uuid,
        number: z.string(),
        deposit: QuoteDeposit.nullable().describe('The quote\'s "Sinal", if it asked for one.'),
      })
      .nullable(),
    shipBy: z.iso.date().nullable(),
    shippingMethod: ShippingMethod.nullable(),
    trackingCode: z.string().nullable(),
    labelFile: StoredFile.nullable(),
    nfStatus: NfStatus,
    nfNumber: z.string().nullable(),
    nfKey: z.string().nullable(),
    nfIssuedOn: z.iso.date().nullable(),
    nfXmlFile: StoredFile.nullable(),
    nfPdfFile: StoredFile.nullable(),
    packagingKit: z
      .object({ id: Uuid, name: z.string(), totalCentavos: z.number().int() })
      .nullable(),
    kitQuantity: z.number().int(),
    charges: OrderCharges.describe('Real charges (the prediction until edited).'),
    predicted: OrderCharges.describe('From the fee version in force on the sale date.'),
    items: z.array(OrderItem),
    itemsSummary: z.string(),
    jobs: z.array(OrderJob),
    checklist: z.array(
      z.object({
        key: ChecklistKey,
        label: z.string(),
        done: z.boolean(),
        doneAt: IsoDateTime.nullable(),
        doneBy: z.string().nullable(),
      }),
    ),
    totalCentavos: z.number().int(),
    profit: OrderProfit,
    ended: z
      .object({
        kind: z.enum(['cancelled', 'returned']),
        reason: z.string(),
        note: z.string().nullable(),
        refundCentavos: z.number().int(),
        backToStock: z.boolean(),
        lossCentavos: z.number().int(),
        at: IsoDateTime,
      })
      .nullable(),
    confirmedAt: IsoDateTime.nullable(),
    closedAt: IsoDateTime.nullable(),
    notes: z.string().nullable(),
    externalRef: z.string().nullable(),
    events: z.array(
      z.object({ at: IsoDateTime, type: z.string(), actor: z.string(), detail: z.unknown() }),
    ),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
  })
  .meta({ id: 'Order' });
export type Order = z.infer<typeof Order>;

export const OrderSummary = Order.pick({
  id: true,
  number: true,
  status: true,
  late: true,
  channel: true,
  marketplaceOrderNo: true,
  soldOn: true,
  customer: true,
  shipBy: true,
  shippingMethod: true,
  trackingCode: true,
  itemsSummary: true,
  totalCentavos: true,
  ended: true,
}).extend({
  production: z.object({
    produced: z.number().int(),
    total: z.number().int(),
    queued: z.number().int(),
  }),
  checklistDone: z.number().int(),
  profitCentavos: z.number().int(),
  fromStock: z.boolean().describe('Some units come from Pronta entrega.'),
  packed: z.boolean().describe('"Embalado" is ticked.'),
  packagingKit: z.object({ id: Uuid, name: z.string() }).nullable(),
});
export type OrderSummary = z.infer<typeof OrderSummary>;

export const OrderList = z.object({
  data: z.array(OrderSummary),
  nextCursor: z.string().nullable(),
  counts: z.record(z.string(), z.number().int()).describe('Per status, plus `late`.'),
});
export const OrderQuery = z.object({
  status: z
    .string()
    .regex(/^[a-z_,]+$/)
    .optional()
    .describe('Comma-separated; default all.'),
  channelId: Uuid.optional(),
  late: z.enum(['true', 'false']).optional(),
  q: z
    .string()
    .trim()
    .max(100)
    .optional()
    .describe('Number, customer, marketplace order number or item.'),
  sort: z.enum(['ship_by', 'recent']).default('ship_by'),
});

export const OrderPreview = z
  .object({
    totalCentavos: z.number().int(),
    estimatedCostCentavos: z.number().int(),
    predicted: OrderCharges,
    estimatedProfitCentavos: z.number().int(),
    estimatedMarginBps: z.number().int(),
    items: z.array(
      z.object({
        description: z.string(),
        quantity: z.number().int(),
        unitPriceCentavos: z.number().int(),
        unitCostCentavos: z.number().int(),
        available: z.number().int().describe('In Pronta entrega now.'),
        fromStock: z.number().int(),
        toPrint: z.number().int(),
        plates: z.number().int(),
      }),
    ),
    effects: z.array(z.string()).describe('"Ao confirmar" lines (pt-BR).'),
  })
  .meta({ id: 'OrderPreview' });
export type OrderPreview = z.infer<typeof OrderPreview>;

export const OrderMessages = z.object({
  inProduction: z.string(),
  shipped: z.string(),
});

export const OrderFileBody = z.object({
  fileId: Uuid,
  role: z.enum(['label', 'nf_xml', 'nf_pdf']),
});
